import { PageTools, type ElementDef } from './pagetools.js';

export type * from './pagetools.ts';

export class GuiTools extends PageTools {
  gtversion = '1.0.3';
  bs = null; // bootstrap dynamically loaded for modal errors etc

  getversion(): Object {
    return {
      'ptversion': this.ptversion,
      'gtversion': this.gtversion
    };
  }

  /**
  * Create a checkbox with a changable class set that can be used for event that will be used for select and deselect single or all 
  * depening on the value set
  * 
  * function needed to be implemented is onchangecheckbox(event)
  * 
  * @param path 
  * @param value if not set use path as value
  * @returns 
  */
  createselectable(path: string, value?: string): string {
    if (value == undefined)
      value = path;
    let html = '<input class="form-check-input changable" type="checkbox" value="'+value+'" checked> <label class="form-check-label">'+path+'</label>';
    return html;
  }

  /**
   * 
   * @param elmid element id or the element to start from 
   * @param localname a name of html type in lower case
   * @returns array of two elements where the first is the element matching the localname type or null and the second how many levels up from the start did we have to go
   * ```
   * function tablecellonclick(event){
   *   const [td, level] = this.findfirstparentoftype(event.target, 'td');
   *   if (td){
   *     // ... do something with table cell
   *   }
   * }
   * ```
   * will find first td up in tree from the event.taget calling it
   */
  findfirstparentoftype(elmid: ElementDef, localname: string): [HTMLElement | null, number] {
    let el = this.elm(elmid);
    if (el){
      let pel = el;
      let level = 0;
      while (pel && pel.localName != localname){
        pel = pel.parentElement;
        level++;
      }
      return [pel, level];
    }
    return [null, 0];
  }

  /**
   * Add or remove color classed on an element. Used to change colors to elements that have a prefix.
   * Ex. btn-danger or text-success
   * The prefix will be added to any classes given if no - in class name so you can use warning instead of btn-warning on a button
   * 
   * By default primary, danger, warning, success and info is always removed before any add
   * 
   * @param elmid element id or the element to change
   * @param what A prefix to use for the classes like btn-
   * @param toremove array of classes or single class to remove or undefined if not used, if no - in name prefix will be added
   * @param toadd array of classes or single class to add or undefined if not used, if no - in name prefix will be added
   * @param text to be added to button instead of the one already there if parameter used
   * @returns 
   */  
  elmremovethenadd(elmid: ElementDef, what: string, toremove: string[] | string | undefined, toadd: string[] | string, text?: string): void {
    let el = this.elm(elmid);
    if (!el)
      return;
    el.classList.remove(what + 'primary');
    el.classList.remove(what + 'danger');
    el.classList.remove(what + 'warning');
    el.classList.remove(what + 'success');
    el.classList.remove(what + 'info');
    if (toremove){
      if (this.isarray(toremove)){
        for (let i=0; i<toremove.length;i++){
          const tr = toremove[i];
          if (typeof tr === 'string') {
            if (tr.indexOf("-") >= 0)
              el.classList.remove(tr);
            else
              el.classList.remove(what + tr);
          }
        }
      } else if (typeof toremove === 'string') {
        if (toremove.indexOf("-") >= 0)
          el.classList.remove(toremove);
        else
          el.classList.remove(what + toremove);
      }
    }
    if (toadd){
      if (this.isarray(toadd)){
        for (let i=0; i<toadd.length;i++){
          const ta = toadd[i];
          if (typeof ta === 'string') {
            if (ta.indexOf("-") >= 0)
              el.classList.add(ta);
            else
              el.classList.add(what + ta);
          }
        }
      } else if (typeof toadd === 'string'){
        if (toadd.indexOf("-") >= 0)
          el.classList.add(toadd);
        else
          el.classList.add(what + toadd);
      }
    }
    if (typeof text == 'string')
      this.html(el,text);
  }
  
  /**
   * Change the color of the text by first remove all text- + primary, danger, warning, success and info classes
   * then add the new class or classes and change the text if you want as well
   * @param elmid element id or the element to change
   * @param toremove array classes or a single class string to remove extra
   * @param toadd array classes or a single class string to add
   * @param text to be added to button instead of the one already there if parameter used
   */
  textremovethenadd(elmid: ElementDef, toremove: string[] | string | undefined, toadd: string[] | string, text?: string): void {
    this.elmremovethenadd(elmid, 'text-', toremove, toadd, text);
  }
  
  /**
   * Change the color of the button by first remove all btn- + primary, danger, warning, success and info classes
   * then add the new class or classes and change the text if you want as well
   * @param elmid buttonid or button to change
   * @param toremove array classes or a single class string to remove extra
   * @param toadd array classes or a single class string to add
   * @param text to be added to button instead of the one already there if parameter used
   */
  btnremovethenadd(elmid: ElementDef, toremove: string[] | string | undefined, toadd:  string[] | string, text?: string): void {
    this.elmremovethenadd(elmid, 'btn-', toremove, toadd, text);
  }

  /**
   * Used by showerror for displaying a error dialog and to avoid a lot of extra code in the budlels we
   * prefer to set it dynamically after creation of guitools. What is used is for now is bootstap.Modal
   * 
   * @param bootstrap the bootstrap element set in window.bootstrap when loading bootrap.js in browser
   */
  setbootstrap(bootstrap){
    this.bs = bootstrap;
  }

  /**
   * Show the error modal that should be defined in the html a id="ErrorModal"
   * Needs to be async to be able to dynamically load bootstrap if not already done
   * @param title The title on top of error
   * @param msg The error message in dialog, can be text or html
   * @param addcancelchain show the chain cancel button or not
   */
  async showerror(title: string, msg: string, addcancelchain?: boolean): Promise<void> {
    this.html("errortitle",title);
    this.html("errormsg",msg);
    if (addcancelchain){
      this.show("cancancelchainbtn");
      this.show("nextinchainbtn");
      this.hide("closeerrorbtn");
    } else {
      this.hide("cancancelchainbtn");
      this.hide("nextinchainbtn");
      this.show("closeerrorbtn");
    }
    if (this.bs)
    {
      const myModal = new this.bs.Modal("#ErrorModal", {});
      myModal.show();
    }
  }
  
  /**
   * For make a part of the page adjust to the resizing of the app
   * 
   * @param elmid element id or the element to resize
   * @param adjust default if 40 px at the bottom is left, you can use a different number by adding it here
   */
  settabsize(elmid: ElementDef, adjust?: number): void {
    let toadjust = 40;
    if (adjust !== undefined && typeof adjust === "number")
      toadjust = adjust;
    let tab = this.elm(elmid);
    if (tab){
      let top = tab.offsetTop;
      let height = window.innerHeight - top - toadjust;
      tab.style.height = '' + height + 'px';
    }
  }

  /**
   * Give to strings in the form of semantic versioning and tell if the first is newer than the second
   * Support any type version as long as they are seperated by the same number of .
   * Ex. 0.1.0 and 0.1.1 gives false
   * Ex. 9.0.0.2992 and 9.0.0.2990 gives true
   * Ex. 9.1.0.0 and 9.0.0.2990 gives true as well
   * 
   * @param latest version to check
   * @param installed version to check against
   * @returns true or false
   */
  isnewer(latest: string, installed: string): boolean {
    let lver = latest.split('.');
    let iver = installed.split('.');
    for (let i = 0; i<lver.length; i++){
      let plver = (typeof lver[i] === 'string' ? parseInt(lver[i] as string) : 0);
      let piver = (typeof iver[i] === 'string' ? parseInt(iver[i] as string) : 0);
      if (iver.length>i && plver>piver){
        return true;
      } else if (!(isNaN(plver) || isNaN(piver))){
        if (plver!=piver)
          return false;
      }
    }
    return false;
  }

    
}
